/**
 * As peças visuais do Guia do MGDLead.
 *
 * Tudo sai do design system (tokens, `Card`, `Badge`, `Dialog`); o que é próprio daqui é a
 * COMPOSIÇÃO: a captura grande com marcadores numerados, o passo em linha do tempo e o recado
 * com ícone + palavra + cor (nunca só cor — DESIGN_SYSTEM §1.3).
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock,
  Lightbulb,
  Maximize2,
  OctagonAlert,
  RefreshCw,
} from 'lucide-react';
import { Badge, Dialog, cn } from '@mgdlead/ui';

import { CopyBlock } from '../../pages/workspace/redirectors/CopyBlock';
import { contarPassos, minutosDeLeitura } from './busca';
import type { CapituloDoGuia, ImagemDoGuia, NotaDoGuia, PassoDoGuia, TomDaNota } from './tipos';
import './guia.css';

/* ========================================================================== */
/*  Recado (dica / atenção / automático / importante)                          */
/* ========================================================================== */

const TOM: Record<
  TomDaNota,
  { palavra: string; icone: typeof Lightbulb; caixa: string; barra: string; texto: string }
> = {
  dica: {
    palavra: 'Dica',
    icone: Lightbulb,
    caixa: 'bg-success-soft',
    barra: 'bg-success',
    texto: 'text-success',
  },
  atencao: {
    palavra: 'Atenção',
    icone: AlertTriangle,
    caixa: 'bg-warning-soft',
    barra: 'bg-warning',
    texto: 'text-warning',
  },
  sozinho: {
    palavra: 'Automático',
    icone: RefreshCw,
    caixa: 'bg-info-soft',
    barra: 'bg-info',
    texto: 'text-info',
  },
  importante: {
    palavra: 'Importante',
    icone: OctagonAlert,
    caixa: 'bg-danger-soft',
    barra: 'bg-danger',
    texto: 'text-danger',
  },
};

export function NotaGuia({ nota }: { nota: NotaDoGuia }) {
  const t = TOM[nota.tom];
  const Icone = t.icone;
  return (
    <aside
      className={cn('relative flex gap-3 overflow-hidden rounded-md py-3 pl-4 pr-3', t.caixa)}
      aria-label={`${t.palavra}: ${nota.titulo}`}
    >
      <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', t.barra)} />
      <Icone aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', t.texto)} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">
          <span className={cn('mr-1.5 font-mono text-2xs uppercase tracking-caps', t.texto)}>
            {t.palavra}
          </span>
          {nota.titulo}
        </p>
        <p className="text-sm text-muted-foreground">{nota.texto}</p>
      </div>
    </aside>
  );
}

/* ========================================================================== */
/*  Captura com marcadores numerados                                           */
/* ========================================================================== */

const DIMENSOES = {
  desktop: { largura: 1280, altura: 800 },
  celular: { largura: 390, altura: 844 },
} as const;

function Marcadores({
  imagem,
  ativo,
  setAtivo,
}: {
  imagem: ImagemDoGuia;
  ativo: number | null;
  setAtivo: (i: number | null) => void;
}) {
  return (
    <>
      {imagem.marcadores?.map((m, i) =>
        m.x === undefined || m.y === undefined ? null : (
          <span
            key={m.rotulo}
            aria-hidden="true"
            data-ativo={ativo === i}
            onMouseEnter={() => setAtivo(i)}
            onMouseLeave={() => setAtivo(null)}
            className={cn(
              'guia-marcador absolute z-10 grid size-5 place-items-center rounded-pill md:size-7',
              'bg-primary text-2xs font-semibold md:text-xs text-primary-foreground nums shadow-md',
              'ring-2 ring-surface',
            )}
            style={
              {
                left: `${m.x}%`,
                top: `${m.y}%`,
                animationDelay: `${120 + i * 90}ms`,
              } as CSSProperties
            }
          >
            {i + 1}
          </span>
        ),
      )}
    </>
  );
}

export function IlustracaoGuia({
  imagem,
  prioridade = false,
  className,
}: {
  imagem: ImagemDoGuia;
  /** A capa do capítulo carrega já; as do meio do texto só quando chegam perto. */
  prioridade?: boolean;
  className?: string;
}) {
  const [ativo, setAtivo] = useState<number | null>(null);
  const [ampliada, setAmpliada] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const dim = DIMENSOES[imagem.formato];
  const celular = imagem.formato === 'celular';
  const marcadores = imagem.marcadores ?? [];

  const quadro = (emDialogo: boolean) => (
    <div
      className={cn(
        'relative overflow-hidden bg-muted',
        celular ? 'rounded-xl ring-4 ring-surface-inverse' : 'rounded-lg ring-1 ring-border',
      )}
      style={{ aspectRatio: `${dim.largura} / ${dim.altura}` }}
    >
      {falhou ? (
        <div className="bg-hatch absolute inset-0 grid place-items-center p-4 text-center">
          <p className="max-w-xs rounded-md bg-surface px-3 py-2 text-xs text-muted-foreground">
            A imagem desta tela ainda não foi gerada. O texto ao lado descreve o que ela mostra.
          </p>
        </div>
      ) : (
        <img
          src={imagem.src}
          alt={imagem.alt}
          width={dim.largura}
          height={dim.altura}
          loading={prioridade || emDialogo ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => setFalhou(true)}
          className="absolute inset-0 size-full object-cover object-top"
        />
      )}
      {falhou ? null : <Marcadores imagem={imagem} ativo={ativo} setAtivo={setAtivo} />}
    </div>
  );

  return (
    <figure className={cn('flex flex-col gap-3', className)}>
      <div className={cn('group relative', celular && 'mx-auto w-full max-w-60')}>
        {quadro(false)}
        {falhou ? null : (
          <button
            type="button"
            onClick={() => setAmpliada(true)}
            className={cn(
              'absolute bottom-2 right-2 z-20 inline-flex items-center gap-1.5 rounded-pill',
              'bg-surface-inverse px-2.5 py-1 text-2xs font-medium text-inverse-foreground',
              'opacity-90 transition-opacity hover:opacity-100 focus-visible:opacity-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            aria-label={`Ampliar a imagem: ${imagem.alt}`}
          >
            <Maximize2 aria-hidden="true" className="size-3" />
            Ampliar
          </button>
        )}
      </div>

      {marcadores.length > 0 || imagem.legenda ? (
        <figcaption className="flex flex-col gap-2">
          {marcadores.length > 0 ? (
            <ol className="flex flex-col gap-1.5">
              {marcadores.map((m, i) => (
                <li
                  key={m.rotulo}
                  onMouseEnter={() => setAtivo(i)}
                  onMouseLeave={() => setAtivo(null)}
                  className={cn(
                    'flex items-start gap-2 rounded-md px-1.5 py-1 text-sm transition-colors',
                    ativo === i && 'bg-primary-soft',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="mt-px grid size-5 shrink-0 place-items-center rounded-pill bg-primary text-2xs font-semibold text-primary-foreground nums"
                  >
                    {i + 1}
                  </span>
                  <span className="text-foreground">
                    <span className="sr-only">Marcador {i + 1}: </span>
                    {m.rotulo}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
          {imagem.legenda ? (
            <p className="text-xs text-muted-foreground">{imagem.legenda}</p>
          ) : null}
        </figcaption>
      ) : null}

      <Dialog
        open={ampliada}
        onOpenChange={setAmpliada}
        title={imagem.alt}
        size="lg"
        className={celular ? undefined : 'md:max-w-5xl'}
      >
        <div className={cn(celular && 'mx-auto w-full max-w-xs')}>
          {ampliada ? quadro(true) : null}
        </div>
      </Dialog>
    </figure>
  );
}

/* ========================================================================== */
/*  Passo                                                                      */
/* ========================================================================== */

function Paragrafos({ texto }: { texto: string }) {
  return (
    <>
      {texto
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => (
          <p key={p.slice(0, 40)} className="max-w-prose text-base text-muted-foreground">
            {p}
          </p>
        ))}
    </>
  );
}

/** "Nome — explicação" vira nome em destaque + explicação. Sem travessão, fica tudo igual. */
function Item({ texto }: { texto: string }) {
  const [nome, ...resto] = texto.split(' — ');
  return (
    <li className="flex gap-2 text-sm">
      <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-pill bg-primary" />
      <span className="text-muted-foreground">
        {resto.length > 0 ? (
          <>
            <strong className="font-medium text-foreground">{nome}</strong> — {resto.join(' — ')}
          </>
        ) : (
          texto
        )}
      </span>
    </li>
  );
}

export function PassoGuia({
  passo,
  numero,
  ultimo,
}: {
  passo: PassoDoGuia;
  numero: number;
  ultimo: boolean;
}) {
  return (
    <li id={passo.id} className="relative scroll-mt-28 pb-10 pl-11 last:pb-2 md:pl-14">
      {/* A linha do tempo: liga um passo ao próximo. */}
      {ultimo ? null : (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-3.5 top-9 w-px bg-border md:left-[1.1875rem]"
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-0 top-0 grid size-7 place-items-center rounded-pill md:size-10',
          'bg-primary-soft font-mono text-xs font-semibold text-primary-text nums md:text-sm',
          'ring-4 ring-background',
        )}
      >
        {String(numero).padStart(2, '0')}
      </span>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1 pt-0.5 md:pt-2">
          {passo.onde ? (
            <p className="font-mono text-2xs uppercase tracking-caps text-subtle-foreground">
              {passo.onde}
            </p>
          ) : null}
          <h3 className="text-lg text-foreground">{passo.titulo}</h3>
        </div>

        <Paragrafos texto={passo.texto} />

        {passo.blocos && passo.blocos.length > 0 ? (
          <dl className="grid gap-2 sm:grid-cols-2">
            {passo.blocos.map((b) => (
              <div key={b.rotulo} className="flex flex-col gap-1 rounded-md bg-surface-subtle p-3">
                <dt className="font-mono text-2xs uppercase tracking-caps text-primary-text">
                  {b.rotulo}
                </dt>
                <dd className="text-sm text-foreground">{b.texto}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {passo.itens && passo.itens.length > 0 ? (
          <ul className="flex max-w-prose flex-col gap-1.5">
            {passo.itens.map((i) => (
              <Item key={i} texto={i} />
            ))}
          </ul>
        ) : null}

        {passo.codigo ? <CopyBlock value={passo.codigo} what="Código" variant="pre" /> : null}

        {passo.imagem ? <IlustracaoGuia imagem={passo.imagem} className="mt-1" /> : null}

        {passo.notas && passo.notas.length > 0 ? (
          <div className="flex flex-col gap-2">
            {passo.notas.map((n) => (
              <NotaGuia key={n.titulo} nota={n} />
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/* ========================================================================== */
/*  Cartão de capítulo                                                         */
/* ========================================================================== */

export function CapituloCard({
  capitulo,
  numero,
  href,
  lido,
}: {
  capitulo: CapituloDoGuia;
  numero: number;
  href: string;
  lido: boolean;
}) {
  const Icone = capitulo.icone;
  return (
    <Link
      to={href}
      className={cn(
        'group flex h-full flex-col gap-4 rounded-lg bg-surface p-4 md:p-5',
        'ring-1 ring-transparent transition-[box-shadow,background-color] hover:ring-border-strong',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="grid size-11 place-items-center rounded-md bg-primary-soft text-primary-text">
          <Icone aria-hidden="true" className="size-5" />
        </span>
        {lido ? (
          <Badge tone="success" dot>
            Lido
          </Badge>
        ) : (
          <span className="font-mono text-xs text-subtle-foreground nums">
            {String(numero).padStart(2, '0')}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <h3 className="text-md text-foreground">{capitulo.titulo}</h3>
        <p className="text-sm text-muted-foreground">{capitulo.resumo}</p>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-subtle-foreground">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <BookOpen aria-hidden="true" className="size-3.5" />
            {contarPassos(capitulo)} {contarPassos(capitulo) === 1 ? 'tópico' : 'tópicos'}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock aria-hidden="true" className="size-3.5" />
            {minutosDeLeitura(capitulo)} min
          </span>
        </span>
        <ArrowRight
          aria-hidden="true"
          className="size-4 text-primary-text transition-transform group-hover:translate-x-0.5"
        />
      </div>
    </Link>
  );
}

/* ========================================================================== */
/*  Barra de progresso dos capítulos                                           */
/* ========================================================================== */

export function ProgressoDoGuia({
  lidos,
  total,
  invertido = false,
}: {
  lidos: number;
  total: number;
  /** Sobre o fundo violeta do destaque. */
  invertido?: boolean;
}) {
  const pct = total === 0 ? 0 : Math.round((lidos / total) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={lidos}
        aria-label="Capítulos do guia lidos"
        className={cn(
          'h-1.5 w-full overflow-hidden rounded-pill',
          invertido ? 'bg-violet-400' : 'bg-muted',
        )}
      >
        <div
          className={cn(
            'h-full rounded-pill transition-[width] duration-500',
            invertido ? 'bg-primary-foreground' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className={cn('text-xs nums', invertido ? 'text-violet-100' : 'text-muted-foreground')}>
        {lidos === 0
          ? `${total} capítulos · nenhum lido ainda`
          : lidos === total
            ? `Os ${total} capítulos lidos`
            : `${lidos} de ${total} capítulos lidos`}
      </p>
    </div>
  );
}

/** Um selo pequeno de "lido" para listas compactas. */
export function SeloLido({ lido, children }: { lido: boolean; children?: ReactNode }) {
  return lido ? (
    <CheckCircle2 aria-label="Lido" className="size-4 shrink-0 text-success" />
  ) : (
    <>{children}</>
  );
}
